import { CheckCircle2, XCircle, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import type {
  MissingExchangeRateRequirement,
  PreflightResult,
} from '@/features/payroll/types/payroll.types'
import { getFxRequirementsFromPreflight } from '@/features/payroll/utils/fxRequirements'
import { PayrollFxRequirementsCard } from './PayrollFxRequirementsCard'

export interface PreflightPanelProps {
  preflight: PreflightResult
  defaultCollapsed?: boolean
  onCreateExchangeRate?: (requirement: MissingExchangeRateRequirement) => void
}

export function PreflightPanel({
  preflight,
  defaultCollapsed = false,
  onCreateExchangeRate,
}: PreflightPanelProps): React.ReactElement {
  const [collapsed, setCollapsed] = useState(defaultCollapsed)
  const { canRun, hardBlocks, warnings } = preflight
  const fxRequirements = getFxRequirementsFromPreflight(preflight)
  const hasIssues = hardBlocks.length > 0 || warnings.length > 0

  if (!hasIssues) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700 dark:border-green-900/50 dark:bg-green-900/20 dark:text-green-300">
        <CheckCircle2 className="h-4 w-4 shrink-0" />
        All preflight checks passed
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700">
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        className="flex w-full items-center gap-2 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700/50"
      >
        {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        {canRun ? (
          <span className="text-amber-600 dark:text-amber-400">
            {warnings.length} warning(s)
          </span>
        ) : (
          <span className="text-red-600 dark:text-red-400">
            {hardBlocks.length} blocking issue(s)
            {warnings.length > 0 && `, ${warnings.length} warning(s)`}
          </span>
        )}
      </button>
      {!collapsed && (
        <div className="space-y-2 border-t border-gray-200 px-4 py-3 dark:border-gray-700">
          {hardBlocks.map((msg, i) => (
            <div key={i} className="flex items-start gap-2 text-sm text-red-700 dark:text-red-300">
              <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {msg.message}
            </div>
          ))}
          {warnings.map((msg, i) => (
            <div key={i} className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-300">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              {msg.message}
            </div>
          ))}
          {fxRequirements && (
            <PayrollFxRequirementsCard
              requirements={fxRequirements}
              tone="danger"
              onCreateExchangeRate={onCreateExchangeRate}
            />
          )}
        </div>
      )}
    </div>
  )
}

PreflightPanel.displayName = 'PreflightPanel'
